import React, { useState, useEffect, useCallback } from "react";
import { Link, useNavigate, useLocation, Outlet } from "react-router-dom";
import { Home, Search, PlusCircle, MessageSquare, User, Heart, Bell, Globe, LogOut, LayoutDashboard, Shield, ChevronRight, LifeBuoy, Store, CreditCard, AlertTriangle } from "lucide-react";
import { useI18n, LANG_LABELS } from "../i18n/I18nContext";
import { useAuth } from "../context/AuthContext";
import api from "../lib/api";
import { timeAgo } from "../lib/format";
import { notifLink, notifLabel } from "../lib/notifications";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator, DropdownMenuLabel,
} from "./ui/dropdown-menu";
import { Avatar, AvatarFallback, AvatarImage } from "./ui/avatar";
import { fileUrl } from "../lib/api";
import { SITE_PAGES } from "../lib/sitePages";

function SiteFooter() {
  const { lang } = useI18n();
  return (
    <footer data-testid="site-footer" className="border-t border-slate-200 bg-white mt-10">
      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <Link to="/" className="flex items-center gap-2">
            <img src="/logo-icon.png" alt="CANARIAS MARKET" className="h-8 w-8 object-contain" />
            <span className="font-display font-extrabold text-base text-slate-900">CANARIAS<span className="text-[#16a34a]"> MARKET</span></span>
          </Link>
          <nav className="flex flex-wrap gap-x-5 gap-y-2">
            {SITE_PAGES.map((p) => (
              <Link key={p.slug} to={p.path} data-testid={`footer-link-${p.slug}`}
                className="text-sm text-slate-500 hover:text-[#008080] transition-colors">
                {p.title[lang] || p.title.es}
              </Link>
            ))}
          </nav>
        </div>
        <p className="text-xs text-slate-400 mt-6">© {new Date().getFullYear()} Canarias Market</p>
      </div>
    </footer>
  );
}

function useCounts(user) {
  const [counts, setCounts] = useState({ messages: 0, notifications: 0 });
  useEffect(() => {
    if (!user) return;
    let active = true;
    const poll = async () => {
      try { const { data } = await api.get("/unread-count"); if (active) setCounts(data); } catch {}
    };
    poll();
    const iv = setInterval(poll, 8000);
    return () => { active = false; clearInterval(iv); };
  }, [user]);
  return counts;
}

function NotificationsBell() {
  const { t, lang } = useI18n();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const load = useCallback(async () => {
    try { const { data } = await api.get("/notifications"); setItems(data); } catch {}
  }, []);
  useEffect(() => { load(); const iv = setInterval(load, 10000); return () => clearInterval(iv); }, [load]);
  const unread = items.filter((i) => !i.read).length;

  const markRead = async () => { try { await api.post("/notifications/read"); load(); } catch {} };

  const onClickNotif = (n) => {
    setOpen(false);
    const link = notifLink(n, user);
    if (link) navigate(link);
  };
  const goToCenter = () => { setOpen(false); navigate("/notifications"); };

  return (
    <DropdownMenu open={open} onOpenChange={(o) => { setOpen(o); if (o) markRead(); }}>
      <DropdownMenuTrigger asChild>
        <button data-testid="notifications-bell" className="relative h-10 w-10 grid place-items-center rounded-full hover:bg-slate-100 transition-colors">
          <Bell className="h-5 w-5 text-slate-700" />
          {unread > 0 && <span className="absolute top-1.5 right-1.5 h-4 min-w-4 px-1 grid place-items-center text-[10px] font-bold text-white bg-rose-500 rounded-full">{unread}</span>}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-80 max-h-96 overflow-y-auto">
        <DropdownMenuLabel className="flex items-center justify-between">
          {t("notifications.title")}
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        {items.length === 0 ? (
          <div className="px-3 py-6 text-center text-sm text-slate-400">{t("notifications.empty")}</div>
        ) : items.slice(0, 12).map((n) => {
          const clickable = !!notifLink(n, user);
          return (
            <button
              key={n.id}
              data-testid={`notification-item-${n.type}`}
              onClick={() => onClickNotif(n)}
              disabled={!clickable}
              className={`w-full text-left px-3 py-2 text-sm border-b border-slate-50 last:border-0 flex flex-col ${clickable ? "hover:bg-teal-50/60 cursor-pointer" : "cursor-default"} ${!n.read ? "bg-teal-50/30" : ""}`}
            >
              <span className="text-slate-800">{notifLabel(n, t)}</span>
              <span className="text-[11px] text-slate-400 mt-0.5 flex items-center gap-1">
                {timeAgo(n.created_at, lang)}
                {clickable && <ChevronRight className="h-3 w-3" />}
              </span>
            </button>
          );
        })}
        <DropdownMenuSeparator />
        <button data-testid="notif-see-all" onClick={goToCenter}
          className="w-full text-center px-3 py-2 text-sm font-medium text-[#008080] hover:bg-teal-50/60">
          {t("notifCenter.seeAll")}
        </button>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export function LanguageSwitcher() {
  const { lang, setLang, languages } = useI18n();
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button data-testid="language-switcher" className="h-10 px-3 flex items-center gap-1.5 rounded-full hover:bg-slate-100 transition-colors text-sm font-medium text-slate-700">
          <Globe className="h-4 w-4" /> <span className="uppercase">{lang}</span>
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        {languages.map((l) => (
          <DropdownMenuItem key={l} data-testid={`lang-option-${l}`} onClick={() => setLang(l)} className={lang === l ? "bg-teal-50 text-teal-700 font-medium" : ""}>
            {LANG_LABELS[l]}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function Brand() {
  return (
    <Link to="/" data-testid="brand-logo" className="flex items-center gap-2 shrink-0">
      <img src="/logo-icon.png" alt="CANARIAS MARKET" className="h-9 w-9 object-contain" />
      <span className="font-display font-extrabold text-lg text-slate-900 hidden sm:block">CANARIAS<span className="text-[#16a34a]"> MARKET</span></span>
    </Link>
  );
}

export default function Layout() {
  const { t } = useI18n();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const loc = useLocation();
  const counts = useCounts(user);
  const [q, setQ] = useState("");

  const doSearch = (e) => {
    e.preventDefault();
    navigate(`/search?q=${encodeURIComponent(q)}`);
  };

  const navItems = [
    { to: "/", icon: Home, label: t("nav.home"), test: "nav-home" },
    { to: "/search", icon: Search, label: t("nav.search"), test: "nav-search" },
    { to: "/sell", icon: PlusCircle, label: t("nav.sell"), test: "nav-sell", highlight: true },
    { to: "/messages", icon: MessageSquare, label: t("nav.messages"), test: "nav-messages", badge: counts.messages },
    { to: user ? "/profile" : "/auth", icon: User, label: t("nav.profile"), test: "nav-profile" },
  ];
  const isActive = (to) => (to === "/" ? loc.pathname === "/" : loc.pathname.startsWith(to));

  return (
    <div className="min-h-screen bg-[#F8FAF9] pb-20 md:pb-0">
      {/* Desktop / top header */}
      <header className="sticky top-0 z-40 backdrop-blur-md bg-white/85 border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 h-16 flex items-center gap-3">
          <Brand />
          <form onSubmit={doSearch} className="flex-1 max-w-xl hidden md:flex items-center gap-2 bg-slate-100 rounded-full pl-4 pr-1.5 h-11 focus-within:ring-2 ring-teal-500/40">
            <Search className="h-4 w-4 text-slate-400" />
            <input data-testid="header-search-input" value={q} onChange={(e) => setQ(e.target.value)}
              placeholder={t("home.searchPlaceholder")} className="flex-1 bg-transparent outline-none text-sm" />
            <button type="submit" data-testid="header-search-btn" className="h-8 px-4 rounded-full bg-[#008080] hover:bg-[#006666] text-white text-sm font-semibold transition-colors shrink-0">{t("common.search")}</button>
          </form>
          <div className="flex-1 md:hidden" />
          <Link to="/servicios" data-testid="nav-services-link" className="hidden md:flex items-center gap-1.5 h-10 px-3 rounded-full text-sm font-semibold text-slate-700 hover:bg-slate-100 transition-colors">
            <Store className="h-4 w-4" /> {t("nav.services")}
          </Link>
          <div className="flex items-center gap-1">
            <LanguageSwitcher />
            {user && (
              <>
                <Link to="/favorites" data-testid="nav-favorites" className="h-10 w-10 hidden sm:grid place-items-center rounded-full hover:bg-slate-100 transition-colors"><Heart className="h-5 w-5 text-slate-700" /></Link>
                <NotificationsBell />
              </>
            )}
            <Link to="/sell" data-testid="header-sell-btn" className="hidden md:flex items-center gap-1.5 h-10 px-4 rounded-full bg-[#008080] hover:bg-[#006666] text-white text-sm font-semibold transition-colors">
              <PlusCircle className="h-4 w-4" /> {t("common.sellNow")}
            </Link>
            {user ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button data-testid="user-menu-trigger" className="ml-1">
                    <Avatar className="h-9 w-9 border border-slate-200">
                      <AvatarImage src={fileUrl(user.profile_photo)} />
                      <AvatarFallback className="bg-teal-100 text-teal-700 text-sm font-semibold">{user.name?.[0]?.toUpperCase()}</AvatarFallback>
                    </Avatar>
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-52">
                  <DropdownMenuLabel className="truncate">{user.name}</DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem data-testid="menu-profile" onClick={() => navigate("/profile")}><User className="h-4 w-4 mr-2" />{t("nav.profile")}</DropdownMenuItem>
                  <DropdownMenuItem data-testid="menu-dashboard" onClick={() => navigate("/dashboard")}><LayoutDashboard className="h-4 w-4 mr-2" />{t("nav.dashboard")}</DropdownMenuItem>
                  <DropdownMenuItem data-testid="menu-plan" onClick={() => navigate("/planes")}><CreditCard className="h-4 w-4 mr-2" />{t("nav.plan")}</DropdownMenuItem>
                  <DropdownMenuItem data-testid="menu-services" onClick={() => navigate("/servicios")}><Store className="h-4 w-4 mr-2" />{t("nav.services")}</DropdownMenuItem>
                  <DropdownMenuItem data-testid="menu-notifications" onClick={() => navigate("/notifications")}><Bell className="h-4 w-4 mr-2" />{t("nav.notifications")}</DropdownMenuItem>
                  <DropdownMenuItem data-testid="menu-support" onClick={() => navigate("/support")}><LifeBuoy className="h-4 w-4 mr-2" />{t("nav.support")}</DropdownMenuItem>
                  <DropdownMenuItem data-testid="menu-favorites" onClick={() => navigate("/favorites")}><Heart className="h-4 w-4 mr-2" />{t("nav.favorites")}</DropdownMenuItem>
                  {user.role === "admin" && <DropdownMenuItem data-testid="menu-admin" onClick={() => navigate("/admin")}><Shield className="h-4 w-4 mr-2" />{t("nav.admin")}</DropdownMenuItem>}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem data-testid="menu-logout" onClick={async () => { await logout(); navigate("/"); }}><LogOut className="h-4 w-4 mr-2" />{t("nav.logout")}</DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <Link to="/auth" data-testid="header-login-btn" className="ml-1 h-10 px-4 grid place-items-center rounded-full border border-slate-300 text-sm font-semibold hover:bg-slate-100 transition-colors">{t("auth.login")}</Link>
            )}
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto">
        {user && user.plan_status === "pending_payment" && loc.pathname !== "/planes" && (
          <button data-testid="pending-payment-banner" onClick={() => navigate("/planes")}
            className="w-full flex items-center justify-center gap-2 bg-amber-500 hover:bg-amber-600 text-white text-sm font-semibold px-4 py-2.5 transition-colors">
            <AlertTriangle className="h-4 w-4" />
            {t("nav.pendingPayment")}
            <ChevronRight className="h-4 w-4" />
          </button>
        )}
        <Outlet />
      </main>

      <SiteFooter />

      {/* Mobile bottom nav */}
      <nav className="fixed bottom-0 left-0 right-0 z-40 bg-white/95 backdrop-blur-lg border-t border-slate-200 md:hidden flex justify-around items-center h-16 px-2">
        {navItems.map((item) => {
          const active = isActive(item.to);
          if (item.highlight) {
            return (
              <Link key={item.to} to={item.to} data-testid={item.test} className="flex flex-col items-center -mt-6">
                <span className="h-14 w-14 grid place-items-center rounded-full bg-[#008080] text-white shadow-lg shadow-teal-900/30 border-4 border-[#F8FAF9]">
                  <item.icon className="h-6 w-6" />
                </span>
                <span className="text-[10px] font-semibold text-[#008080] mt-0.5">{item.label}</span>
              </Link>
            );
          }
          return (
            <Link key={item.to} to={item.to} data-testid={item.test} className={`relative flex flex-col items-center gap-0.5 px-2 py-1 ${active ? "text-[#008080]" : "text-slate-500"}`}>
              <item.icon className="h-[22px] w-[22px]" />
              <span className="text-[10px] font-medium">{item.label}</span>
              {item.badge > 0 && <span className="absolute top-0 right-1 h-4 min-w-4 px-1 grid place-items-center text-[9px] font-bold text-white bg-rose-500 rounded-full">{item.badge}</span>}
            </Link>
          );
        })}
      </nav>
    </div>
  );
}
